import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import {
  Home as HomeIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <IconButton edge="start" color="primary" sx={{ mr: 2 }} onClick={() => navigate('/')}>
            <HomeIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 600 }}>
            QuickBuildr.ai
          </Typography>
          {isAuthenticated ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Button
                color="primary"
                sx={{ mr: 2 }}
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/register')}
              >
                Get Started
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Container maxWidth="md">
        <Box sx={{ py: { xs: 6, md: 8 } }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="h2" fontWeight={700} color="primary.main" gutterBottom>
              Privacy Policy
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Last updated: January 27, 2025
            </Typography>

            <Paper elevation={0} sx={{ p: 4, bgcolor: 'white' }}>
              <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mt: 4 }}>
                1. Information We Collect
              </Typography>
              <Typography variant="body1" paragraph>
                We collect information you provide directly to us, such as when you create an account, 
                use our services, or contact us for support. This includes:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>Name and contact information (email address, phone number)</li>
                <li>Account credentials (username and password)</li>
                <li>Property search history and saved properties</li>
                <li>Payment information (processed securely through Stripe)</li>
                <li>Communications with our support team</li>
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                2. How We Use Your Information
              </Typography>
              <Typography variant="body1" paragraph>
                We use the information we collect to:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send related information</li>
                <li>Send technical notices and support messages</li>
                <li>Respond to your comments and questions</li>
                <li>Analyze usage patterns to improve user experience</li>
                <li>Detect and prevent fraudulent transactions</li>
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                3. Data Sources
              </Typography>
              <Typography variant="body1" paragraph>
                QuickBuildr.ai accesses public property data from:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>Massachusetts GIS (MassGIS) public databases</li>
                <li>Municipal ArcGIS services</li>
                <li>Public property records and assessments</li>
                <li>Zoning ordinances and building codes</li>
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                We do not sell, trade, or otherwise transfer your personal information to third parties.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                4. Data Security
              </Typography>
              <Typography variant="body1" paragraph>
                We implement appropriate technical and organizational measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. These include:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>End-to-end encryption for data transmission</li>
                <li>Secure storage with encryption at rest</li>
                <li>Regular security audits and updates</li>
                <li>Limited access to personal information by employees</li>
                <li>Compliance with industry security standards</li>
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                5. Data Retention
              </Typography>
              <Typography variant="body1" paragraph>
                We retain your personal information for as long as necessary to provide our services 
                and fulfill the purposes outlined in this privacy policy. You may request deletion 
                of your account and associated data at any time by contacting sales@quickbuildr.ai or calling 781-528-3339.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                6. Your Rights
              </Typography>
              <Typography variant="body1" paragraph>
                You have the right to:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to processing of your data</li>
                <li>Export your data in a portable format</li>
                <li>Withdraw consent at any time</li>
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                7. Cookies and Tracking
              </Typography>
              <Typography variant="body1" paragraph>
                We use cookies and similar tracking technologies to track activity on our service 
                and hold certain information. You can instruct your browser to refuse all cookies 
                or to indicate when a cookie is being sent.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                8. Third-Party Services
              </Typography>
              <Typography variant="body1" paragraph>
                We may employ third-party companies and individuals to facilitate our service. 
                These third parties have access to your personal information only to perform 
                these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                9. Children's Privacy
              </Typography>
              <Typography variant="body1" paragraph>
                Our service is not intended for use by children under the age of 18. We do not 
                knowingly collect personal information from children under 18.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                10. Changes to This Policy
              </Typography>
              <Typography variant="body1" paragraph>
                We may update our Privacy Policy from time to time. We will notify you of any 
                changes by posting the new Privacy Policy on this page and updating the 
                "Last updated" date.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                11. Contact Us
              </Typography>
              <Typography variant="body1" paragraph>
                If you have any questions about this Privacy Policy, please contact us:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>By phone: 781-528-3339</li>
                <li>By email: sales@quickbuildr.ai</li>
                <li>By mail: QuickBuildr.ai, Boston, MA</li>
                <li>Through our contact form at quickbuildr.ai/contact</li>
              </Typography>
            </Paper>
          </motion.div>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white', py: 4, mt: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" textAlign="center" sx={{ opacity: 0.8 }}>
            © 2025 QuickBuildr.ai. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default PrivacyPage;